// JsonViewer.tsx
import React, { useState } from "react";

type JsonValue =
    | string | number | boolean | null
    | JsonValue[]
    | { [key: string]: JsonValue };

interface JsonNodeProps {
    data: JsonValue;
    nodeKey?: string;
    depth?: number;
}

const JsonNode: React.FC<JsonNodeProps> = ({ data, nodeKey, depth = 0 }) => {
    const type = data === null ? "null" : Array.isArray(data) ? "array" : typeof data;
    const isCollapsible = type === "object" || type === "array";
    const [open, setOpen] = useState(depth < 1);

    const bracket = type === "array" ? ["[", "]"] : ["{", "}"];
    const entries = isCollapsible ? Object.entries(data as object) : [];

    const renderLeaf = () => {
        if (type === "string") return <span style={{ color: "#059669" }}>"{String(data)}"</span>;
        if (type === "number") return <span style={{ color: "#D97706" }}>{String(data)}</span>;
        if (type === "boolean") return <span style={{ color: "#2563EB" }}>{String(data)}</span>;
        if (type === "null") return <span style={{ color: "#9CA3AF" }}>null</span>;
    };

    const KeyLabel = nodeKey !== undefined
        ? <><span style={{ color: "#7C3AED" }}>"{nodeKey}"</span><span style={{ color: "#6B7280" }}>:&nbsp;</span></>
        : null;

    if (!isCollapsible) {
        return (
            <div style={{ display: "flex" }}>
                <span style={{ width: 14 }} />
                {KeyLabel}
                {renderLeaf()}
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }} onClick={() => setOpen(o => !o)}>
                <span style={{ width: 14, fontSize: 9, color: "#9CA3AF", paddingTop: 3, userSelect: "none" }}>
                    {open ? "▼" : "▶"}
                </span>
                {KeyLabel}
                <span style={{ color: "#6B7280" }}>{bracket[0]}</span>
                {!open && <span style={{ color: "#9CA3AF", fontSize: 11 }}>&nbsp;{entries.length} {type === "array" ? "items" : "keys"}&nbsp;</span>}
                {!open && <span style={{ color: "#6B7280" }}>{bracket[1]}</span>}
            </div>

            {open && (
                <>
                    <div style={{ paddingLeft: 14, borderLeft: "1px dashed #E5E7EB", marginLeft: 7 }}>
                        {entries.map(([k, v]) => (
                            <JsonNode
                                key={k}
                                data={v}
                                nodeKey={type === "array" ? undefined : k}
                                depth={depth + 1}
                            />
                        ))}
                    </div>
                    <div style={{ display: "flex" }}>
                        <span style={{ width: 14 }} />
                        <span style={{ color: "#6B7280" }}>{bracket[1]}</span>
                    </div>
                </>
            )}
        </div>
    );
};

interface JsonViewerProps {
    data: unknown;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ data }) => {
    let parsed: JsonValue;
    try {
        parsed = typeof data === "string" ? JSON.parse(data) : (data as JsonValue);
    } catch {
        return <code style={{ color: "red", fontSize: 11 }}>Invalid JSON</code>;
    }

    return (
        <div style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.7 }}>
            <JsonNode data={parsed} />
        </div>
    );
};

export default JsonViewer;